/**
 * Extracts all default values from component metadata.
 * Creates an object mapping argument names to their default values.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.meta - Metadata object containing argument definitions
 * @returns {Object} Object with argument names as keys and default values as values
 *
 * @example
 * const defaults = getAllDefaultValuesFromMetadata({
 *   meta: {
 *     size: { type: STRING, defaultValue: 'medium' },
 *     visible: { type: BOOLEAN, defaultValue: true }
 *   }
 * });
 * // Returns: { size: 'medium', visible: true }
 */
export function getAllDefaultValuesFromMetadata({ meta }) {
  const defaults = {};

  getArgumentNamesFromMetadata({ meta }).forEach(key => {
    defaults[key] = meta[key].defaultValue;
  });

  return defaults;
}

/**
 * Extracts initial values for interactive documentation examples.
 * Prefers initialValue over defaultValue to provide better UX in styleguide.
 * Use this for setting up interactive API examples where you want a non-default
 * starting value that demonstrates the component better.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.meta - Metadata object containing argument definitions
 * @returns {Object} Object with argument names as keys and initial/default values
 *
 * @example
 * const initial = getInitialValuesForDocumentation({
 *   meta: {
 *     size: { type: STRING, defaultValue: 'medium' },
 *     label: { type: STRING, initialValue: 'Save' }
 *   }
 * });
 * // Returns: { size: 'medium', label: 'Save' }
 */
export function getInitialValuesForDocumentation({ meta }) {
  const values = {};

  getArgumentNamesFromMetadata({ meta }).forEach(key => {
    values[key] = meta[key].initialValue ?? meta[key].defaultValue;
  });

  return values;
}

/**
 * Extracts all argument names from component metadata.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.meta - Metadata object containing argument definitions
 * @returns {Array<string>} Array of argument names
 *
 * @example
 * const names = getArgumentNamesFromMetadata({
 *   meta: { size: {...}, visible: {...} }
 * });
 * // Returns: ['size', 'visible']
 */
export function getArgumentNamesFromMetadata({ meta }) {
  if (!meta) return [];

  return Object.keys(meta);
}

/**
 * Gets the default value for a specific argument from component metadata.
 *
 * @param {Object} metadata - Metadata object with meta property
 * @param {string} argument - Name of the argument to get default value for
 * @returns {*} The default value for the specified argument
 *
 * @example
 * const defaultSize = getDefaultValueFromMetadata(METADATA, 'size');
 * // Returns: 'medium'
 */
export function getDefaultValueFromMetadata(metadata, argument) {
  return getAllDefaultValuesFromMetadata(metadata)[argument];
}

/**
 * Merges parent class metadata with child class metadata.
 * Child's top-level properties (component, description, name, resources) are preserved.
 * The meta objects from parent and child are merged, with child properties taking precedence.
 * Useful for extending base component metadata in derived components.
 *
 * @param {Object} parentMetadata - Parent class metadata object
 * @param {Object} childMetadata - Child class metadata object
 * @returns {Object} Merged metadata object with child's top-level props and merged meta
 *
 * @example
 * const merged = mergeMetadata(
 *   { component: 'Base', meta: { id: {...} } },
 *   { component: 'Spds::Action::Button::Close', meta: { ariaLabel: {...} } }
 * );
 * // Returns: {
 * //   component: 'Spds::Action::Button::Close',  // child's component name
 * //   meta: { id: {...}, ariaLabel: {...} }      // merged meta from both
 * // }
 *
 * @example
 * // Child with no meta property (e.g., button variants)
 * const merged = mergeMetadata(
 *   { component: 'Base', meta: { id: {...} } },
 *   { component: 'Spds::Action::Button::Primary', name: 'Primary Button' }
 * );
 * // Returns: {
 * //   component: 'Spds::Action::Button::Primary',
 * //   name: 'Primary Button',
 * //   meta: { id: {...} }  // inherited from parent
 * // }
 */
export function mergeMetadata(parentMetadata = { meta: {} }, childMetadata = { meta: {} }) {
  return {
    ...childMetadata,
    meta: {
      ...parentMetadata?.meta,
      ...childMetadata?.meta,
    },
    resources: {
      ...parentMetadata?.resources,
      ...childMetadata?.resources,
    },
  };
}

/**
 * Marks all arguments in a metadata object as private.
 * Private arguments are not exposed in the component's public API
 * and are used for internal implementation details.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.meta - Metadata object containing argument definitions
 * @returns {Object} New metadata object with all arguments marked as private
 *
 * @example
 * const privateArgs = makeArgumentPrivate({
 *   meta: {
 *     internalFlag: { type: BOOLEAN, defaultValue: false }
 *   }
 * });
 * // Returns: { internalFlag: { type: BOOLEAN, defaultValue: false, private: true } }
 */
export function makeArgumentsPrivate({ meta }) {
  let result = {};

  Object.entries(meta).forEach(([key, metaItem]) => {
    result[key] = { ...metaItem, private: true };
  });

  return { meta: result };
}

/**
 * Helper to pick specific args from a shared config
 *
 * @param {Object} sharedMetadata - Source config object
 * @param {Array<string>} argNames - Prop names to include
 * @returns {Object} Metadata with only specified args
 *
 * @example
 * const gapOnly = pickArgs(sharedProps.flex, ['gap']);
 */
export function pickArgs(sharedMetadata, argNames) {
  const picked = { meta: {}, defaults: {} };

  argNames.forEach(name => {
    if (sharedMetadata.meta?.[name]) {
      picked.meta[name] = sharedMetadata.meta[name];
    }
  });

  return picked;
}

/**
 * Prepares metadata for documentation by filtering out private arguments.
 * Only includes arguments that are part of the public API.
 * Preserves all metadata properties including initialValue.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.meta - Metadata object containing argument definitions
 * @returns {Object} Metadata object with only public arguments
 *
 * @example
 * const docsData = setupDocumentationData({
 *   meta: {
 *     size: { type: STRING, defaultValue: 'medium' },
 *     label: { type: STRING, initialValue: 'Save' },
 *     _internal: { type: BOOLEAN, private: true }
 *   }
 * });
 * // Returns: { size: { type: STRING, defaultValue: 'medium' }, label: { type: STRING, initialValue: 'Save' } }
 */
export function setupDocumentationData({ meta }) {
  const argsForDocs = {};

  getArgumentNamesFromMetadata({ meta }).forEach(key => {
    if (meta[key].private) return;

    argsForDocs[key] = { ...meta[key] };
  });

  return argsForDocs;
}

/**
 * Sorts metadata arguments alphabetically by key.
 * Optionally merges with parent metadata when provided.
 * Useful for consistent ordering in component metadata definitions.
 *
 * @param {Object} metadata - Metadata object with meta property
 * @param {Object} parentMetadata - Optional parent metadata to merge with
 * @returns {Object} Metadata object with alphabetically sorted keys
 *
 * @example
 * const sorted = sortMetadata({
 *   meta: { zIndex: {...}, background: {...}, padding: {...} }
 * });
 * // Returns: { meta: { background: {...}, padding: {...}, zIndex: {...} } }
 *
 * @example
 * // With parent metadata merging
 * const sorted = sortMetadata(METADATA, Super.metadata);
 * // Merges parent and child metadata, then sorts alphabetically
 */
export function sortMetadata(metadata, parentMetadata) {
  const merged = parentMetadata ? mergeMetadata(parentMetadata, metadata) : metadata;

  if (!merged || !merged.meta) {
    return merged;
  }

  const sortedKeys = Object.keys(merged.meta).sort();

  const sortedMeta = {};
  sortedKeys.forEach(key => {
    sortedMeta[key] = merged.meta[key];
  });

  return {
    ...merged,
    meta: sortedMeta,
  };
}

export default {
  getAllDefaultValuesFromMetadata,
  getArgumentNamesFromMetadata,
  getDefaultValueFromMetadata,
  getInitialValuesForDocumentation,
  makeArgumentsPrivate,
  mergeMetadata,
  pickArgs,
  setupDocumentationData,
  sortMetadata,
};
